import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from "recharts";
import { 
  ArrowLeft,
  Trophy,
  Target,
  BookOpen,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Zap,
  RefreshCcw,
  XCircle,
  Lightbulb,
  Lock,
  LogIn
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Colorful brain logo
const BRAIN_LOGO = "https://customer-assets.emergentagent.com/job_e95fcea1-1d05-419a-a729-ac97ff58a502/artifacts/1u3r91fj_logo%20no%20back_Image_Jan_4__2026_at_02_55_25_PM-removebg-preview.png";

const COLORS = ['#7c3aed', '#10b981', '#f59e0b', '#ef4444', '#3b82f6'];

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, authChecked } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [weakAreas, setWeakAreas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPractice, setLoadingPractice] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      const [analyticsRes, weakAreasRes] = await Promise.all([
        axios.get(`${API}/analytics`),
        axios.get(`${API}/weak-areas`)
      ]);
      setAnalytics(analyticsRes.data);
      setWeakAreas(weakAreasRes.data);
    } catch (error) {
      toast.error("Failed to load analytics");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const startWeakAreasPractice = async () => {
    setLoadingPractice(true);
    try {
      const response = await axios.get(`${API}/practice/weak-areas`);
      navigate("/quiz/weak-areas-practice", { state: { practiceQuiz: response.data } });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate practice quiz");
    } finally {
      setLoadingPractice(false);
    }
  };

  const getCategoryChartData = () => {
    if (!analytics?.category_performance) return [];
    return Object.entries(analytics.category_performance).map(([name, data]) => ({
      name: name.length > 12 ? name.substring(0, 12) + '...' : name,
      fullName: name,
      average: Math.round(data.average),
      quizzes: data.total
    }));
  };

  const getRecentTrend = () => {
    if (!analytics?.recent_attempts) return [];
    return analytics.recent_attempts.slice(0, 10).reverse().map((attempt, index) => ({
      attempt: index + 1,
      score: attempt.percentage
    }));
  };

  const getAccuracyData = () => {
    if (!analytics) return [];
    const correct = analytics.correct_answers;
    const incorrect = analytics.total_questions_answered - correct;
    return [
      { name: 'Correct', value: correct },
      { name: 'Incorrect', value: incorrect }
    ];
  };

  const getPriorityColor = (priority) => {
    return priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 
           'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  };

  // Show login required screen if not authenticated
  if (authChecked && !authLoading && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50/30 via-white to-teal-50/20 flex items-center justify-center p-6">
        <motion.div 
          className="max-w-md w-full text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-amber-100 p-8 shadow-lg">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-teal-100 flex items-center justify-center">
              <Lock className="w-8 h-8 text-teal-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">Login Required</h1>
            <p className="text-slate-600 mb-6">
              Sign in to view your analytics and track your progress toward BCBA certification.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate("/auth")}
                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-6 font-semibold"
                data-testid="login-btn"
              >
                <LogIn className="w-5 h-5 mr-2" />
                Sign In / Create Account
              </Button>
              <Button 
                variant="ghost"
                onClick={() => navigate("/")}
                className="w-full text-slate-600"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="animate-spin w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const hasData = analytics?.total_quizzes_taken > 0;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate("/")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl overflow-hidden">
                  <img src={BRAIN_LOGO} alt="Brain Logo" className="w-full h-full object-cover" />
                </div>
                <span className="font-bold text-xl tracking-tight">Analytics</span>
              </div>
            </div>
            <Button 
              onClick={() => navigate("/quizzes")}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="practice-btn"
            >
              Practice Now
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {!hasData ? (
          <motion.div 
            className="text-center py-20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <BookOpen className="w-20 h-20 mx-auto text-slate-300 dark:text-slate-600 mb-6" />
            <h2 className="text-2xl font-bold mb-2" data-testid="no-data-title">No Quiz Data Yet</h2>
            <p className="text-slate-500 mb-6">Complete some quizzes to see your performance analytics</p>
            <Button 
              onClick={() => navigate("/quizzes")}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="start-first-quiz-btn"
            >
              Take Your First Quiz
            </Button>
          </motion.div>
        ) : (
          <>
            {/* Stats Grid */}
            <motion.div 
              className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="stat-card bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800">
                <div className="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mb-4">
                  <BookOpen className="w-6 h-6 text-violet-600" />
                </div>
                <p className="text-3xl font-bold" data-testid="total-quizzes">{analytics.total_quizzes_taken}</p>
                <p className="text-sm text-slate-500">Quizzes Completed</p>
              </div>

              <div className="stat-card bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <p className="text-3xl font-bold" data-testid="average-score">{analytics.average_score}%</p>
                <p className="text-sm text-slate-500">Average Score</p>
              </div>

              <div className="stat-card bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800">
                <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <p className="text-3xl font-bold" data-testid="total-questions">{analytics.total_questions_answered}</p>
                <p className="text-sm text-slate-500">Questions Answered</p>
              </div>

              <div className="stat-card bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800">
                <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-6 h-6 text-amber-600" />
                </div>
                <p className="text-3xl font-bold" data-testid="correct-answers">{analytics.correct_answers}</p>
                <p className="text-sm text-slate-500">Correct Answers</p>
              </div>
            </motion.div>

            {/* Weak Areas Section */}
            {weakAreas && weakAreas.total_incorrect > 0 && (
              <motion.div 
                className="bg-gradient-to-br from-rose-50 to-amber-50 dark:from-rose-950/30 dark:to-amber-950/30 rounded-2xl p-6 border border-rose-200 dark:border-rose-800 mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-rose-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg" data-testid="weak-areas-title">Weak Areas Identified</h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {weakAreas.total_incorrect} questions missed across your attempts
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={startWeakAreasPractice}
                    disabled={loadingPractice}
                    className="bg-rose-600 hover:bg-rose-700"
                    data-testid="practice-weak-areas-btn"
                  >
                    {loadingPractice ? (
                      <>
                        <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Practice Weak Areas
                      </>
                    )}
                  </Button>
                </div>

                {/* Recommendations */}
                {weakAreas.recommendations.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Recommendations
                    </h4>
                    <div className="space-y-2">
                      {weakAreas.recommendations.map((rec, index) => (
                        <div 
                          key={index}
                          className="flex items-center gap-3 p-3 bg-white/60 dark:bg-slate-800/60 rounded-xl"
                        >
                          <Badge className={getPriorityColor(rec.priority)}>
                            {rec.priority}
                          </Badge>
                          <span className="text-sm">{rec.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Weak Categories */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                      Categories by Error Rate
                    </h4>
                    <div className="space-y-3">
                      {weakAreas.weak_categories.slice(0, 5).map((cat, index) => (
                        <div key={index} className="bg-white/60 dark:bg-slate-800/60 rounded-xl p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">{cat.category}</span>
                            <span className={`text-sm font-bold ${
                              cat.error_rate >= 50 ? 'text-rose-600' : 
                              cat.error_rate >= 30 ? 'text-amber-600' : 'text-emerald-600'
                            }`}>
                              {cat.error_rate}% errors
                            </span>
                          </div>
                          <Progress 
                            value={100 - cat.error_rate} 
                            className="h-2"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            {cat.incorrect} incorrect out of {cat.total} attempts
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                      Most Missed Questions
                    </h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {weakAreas.missed_questions.slice(0, 5).map((q, index) => (
                        <div 
                          key={index}
                          className="bg-white/60 dark:bg-slate-800/60 rounded-xl p-3"
                        >
                          <div className="flex items-start gap-2">
                            <XCircle className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm line-clamp-2">{q.question_text}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs">
                                  {q.category}
                                </Badge>
                                <span className="text-xs text-slate-500">
                                  Missed {q.times_missed}x
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Charts */}
            <div className="grid lg:grid-cols-2 gap-6 mb-8">
              {/* Category Performance */}
              <motion.div 
                className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h3 className="font-semibold text-lg mb-6" data-testid="category-chart-title">Performance by Category</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={getCategoryChartData()}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value, name, props) => [value + '%', props.payload.fullName]}
                      />
                      <Bar dataKey="average" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>

              {/* Accuracy Pie Chart */}
              <motion.div 
                className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h3 className="font-semibold text-lg mb-6" data-testid="accuracy-chart-title">Overall Accuracy</h3>
                <div className="h-64 flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getAccuracyData()}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {getAccuracyData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={index === 0 ? '#10b981' : '#ef4444'} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-6 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <span className="text-sm text-slate-500">Correct</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-rose-500" />
                    <span className="text-sm text-slate-500">Incorrect</span>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Score Trend */}
            {getRecentTrend().length > 1 && (
              <motion.div 
                className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <h3 className="font-semibold text-lg mb-6" data-testid="trend-chart-title">Score Trend</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={getRecentTrend()}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                      <XAxis dataKey="attempt" tick={{ fontSize: 12 }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="score" 
                        stroke="#7c3aed" 
                        strokeWidth={3}
                        dot={{ fill: '#7c3aed', strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>
            )}

            {/* Recent Attempts */}
            <motion.div 
              className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <h3 className="font-semibold text-lg mb-6" data-testid="recent-attempts-title">Recent Quiz Attempts</h3>
              <div className="space-y-4">
                {analytics.recent_attempts?.slice(0, 5).map((attempt, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl"
                    data-testid={`recent-attempt-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        attempt.percentage >= 75 
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600' 
                          : attempt.percentage >= 50 
                          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600'
                          : 'bg-rose-100 dark:bg-rose-900/30 text-rose-600'
                      }`}>
                        <Trophy className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium">{attempt.quiz_title}</p>
                        <p className="text-sm text-slate-500">{attempt.category}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${
                        attempt.percentage >= 75 ? 'text-emerald-600' : 
                        attempt.percentage >= 50 ? 'text-amber-600' : 'text-rose-600'
                      }`}>
                        {attempt.percentage}%
                      </p>
                      <p className="text-sm text-slate-500">
                        {attempt.score}/{attempt.total_questions} correct
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </main>
    </div>
  );
}
